import type { FC, PropsWithChildren, ReactNode } from 'react';
import { useRef } from 'react';

import { Typography } from '@mui/material';
import { clsx } from 'clsx';
import { motion, useReducedMotion } from 'framer-motion';
import { useResizeObserver } from 'usehooks-ts';

import { useScreenSize } from 'shared/lib/screen/useScreenSize';

import { RequiredMark } from './RequiredMark';

// The design's collapse curve. Matches the checklist rows, which `shared` cannot import from.
const MESSAGE_TRANSITION = { duration: 0.24, ease: [0.22, 0.7, 0.3, 1] } as const;

export type FieldOrientation = 'horizontal' | 'vertical' | 'inline' | 'inline-reverse' | 'inline-stacked';

// Sizes the L-curve connector to match the parent's flex/grid gap.
// `top = -gap`, `height = gap + 9` (label baseline offset). Pick the size that
// matches the parent container's gap utility.
export type FieldIndentSize = 'sm' | 'lg';

export type FieldLabelVariant = 'default' | 'section' | 'header';

const ORIENTATION_CLASSES: Record<FieldOrientation, string> = {
  horizontal: 'grid grid-cols-2 items-start gap-x-6',
  vertical: 'flex flex-col gap-1',
  // Label+description stretches; the trailing control hugs its content (e.g. a Switch).
  inline: 'grid grid-cols-[1fr_auto] items-start gap-x-6',
  // Mirror of `inline` — the leading control hugs its content, label+description follows.
  'inline-reverse': 'grid grid-cols-[auto_1fr] items-start gap-x-6',
  // Like `inline`, but the description drops to a second row spanning both columns so it
  // wraps under the trailing control rather than being constrained to the label column.
  'inline-stacked': 'grid grid-cols-[1fr_auto] items-center gap-x-6 gap-y-1',
};

const INDENT_CURVE_CLASSES: Record<FieldIndentSize, string> = {
  // tight curve for compact rows -- reaches -10px up and 20px down
  sm: "before:-top-2.5 before:h-5 before:content-['']",
  // 24px parent gap (gap-6): curve reaches -24px up and 33px down to the label baseline
  lg: "before:-top-6 before:h-[33px] before:content-['']",
};

type FieldProps = PropsWithChildren<{
  label?: ReactNode;
  /** Marks the label; the rule that enforces it belongs with the field's validation */
  required?: boolean;
  description?: ReactNode;
  error?: ReactNode;
  /** An advisory note in amber: the value is usable, but something about it is worth knowing */
  warning?: ReactNode;
  helperText?: ReactNode;
  orientation?: FieldOrientation;
  className?: string;
  htmlFor?: string;
  // `true` is a shortcut for `'sm'` (matches a gap-4 parent).
  indent?: boolean | FieldIndentSize;
  // Node rendered to the right of the label. Hidden until something inside the
  // field gains focus (CSS `:focus-within`), useful for context links like
  // "Add dynamic text" that should only appear while the input is active.
  labelAction?: ReactNode;
  // Keeps `labelAction` on screen at rest, for an action that is the point of the
  // row rather than a hint about the control below it.
  keepLabelActionVisible?: boolean;
  labelVariant?: FieldLabelVariant;
}>;

export const Field: FC<FieldProps> = ({
  label,
  required = false,
  description,
  error,
  warning,
  helperText,
  orientation: declaredOrientation = 'vertical',
  className,
  htmlFor,
  indent = false,
  labelAction,
  keepLabelActionVisible = false,
  labelVariant = 'default',
  children,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const messageRef = useRef<HTMLDivElement>(null);
  const { height: messageHeight } = useResizeObserver({ ref: messageRef, box: 'border-box' });
  const { isMobile } = useScreenSize();

  // Two columns need a width a phone has not got, so a horizontal row stacks instead.
  const orientation = isMobile && declaredOrientation === 'horizontal' ? 'vertical' : declaredOrientation;

  const isReversed = orientation === 'inline-reverse';
  const isStacked = orientation === 'inline-stacked';
  const isHeaderLabel = labelVariant === 'header';
  const isSectionLabel = labelVariant === 'section';
  const isLabelLarge = (orientation === 'inline' && !description) || isReversed || isStacked;
  // For `inline-stacked`, the description renders in a second grid row spanning both
  // columns instead of nesting under the label, so it can wrap under the trailing control.
  const renderDescriptionInLabelGroup = !isStacked;
  const indentSize: FieldIndentSize | null = indent === true ? 'sm' : indent === false ? null : indent;
  // In horizontal orientation we don't want to shift the input column; the L-curve attaches to
  // the label cell only so non-indented rows can keep their input alignment.
  const isHorizontal = orientation === 'horizontal';
  const indentCurveClasses = indentSize && [
    'before:absolute before:left-0 before:w-4',
    INDENT_CURVE_CLASSES[indentSize],
    'before:rounded-bl-lg before:border-grey30 before:border-b-[1.5px] before:border-l-[1.5px]',
  ];
  const indentContinuationClasses = indentSize && [
    "after:absolute after:top-px after:-bottom-5 after:left-0 after:w-[1.5px] after:bg-grey30 after:content-['']",
    '[&:not(:has(+_.field-indent))]:after:hidden',
  ];

  return (
    <div
      className={clsx(
        'group w-full',
        ORIENTATION_CLASSES[orientation],
        indentSize && [
          // `field-indent` marker class lets the next sibling check below find another indented field.
          'field-indent relative',
          // In non-horizontal layouts, indent the whole row and draw the curve + continuation on the outer.
          !isHorizontal && ['pl-6', indentCurveClasses, indentContinuationClasses],
          // In horizontal layouts, only the continuation line needs the outer (it spans between rows).
          isHorizontal && indentContinuationClasses,
        ],
        className,
      )}
    >
      {(label || (description && renderDescriptionInLabelGroup)) && (
        <div
          className={clsx(
            'flex flex-col gap-0.5',
            isReversed && 'order-last',
            // In horizontal layouts the L-curve attaches to the label cell so the input column stays aligned.
            indentSize && isHorizontal && ['relative pl-6', indentCurveClasses],
          )}
        >
          <div
            className={clsx(
              'flex',
              // A revealed hint aligns to the label's baseline; a standing control is taller than
              // the label, so it centres instead of hanging below it.
              keepLabelActionVisible ? 'items-center' : 'items-baseline',
              labelAction && 'justify-between gap-2',
            )}
          >
            {label && (
              <Typography
                className={clsx(
                  !isHeaderLabel &&
                    (isSectionLabel || isLabelLarge ? 'text-base leading-6' : 'font-medium text-sm leading-[18px]'),
                )}
                component="label"
                htmlFor={htmlFor}
                variant={isHeaderLabel ? 'h2' : undefined}
              >
                {label}
                {required && <RequiredMark />}
              </Typography>
            )}
            {labelAction && (
              <div
                className={clsx(
                  !keepLabelActionVisible &&
                    'pointer-events-none opacity-0 transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100',
                )}
              >
                {labelAction}
              </div>
            )}
          </div>
          {description && renderDescriptionInLabelGroup && (
            <Typography className="text-grey60" variant={isHeaderLabel ? 'body2' : 'caption'}>
              {description}
            </Typography>
          )}
        </div>
      )}
      <div className="flex flex-col">
        {children}
        {/* The slot holds an error, a warning, a length hint, or nothing at all — and a hint that
            renders null is indistinguishable from one that renders text until it is measured, so
            the height is read off the content rather than declared. `auto` until the first
            measurement lands, so a field that arrives with an error is not animated open. */}
        <motion.div
          className="overflow-hidden"
          initial={false}
          animate={{ height: messageHeight ?? 'auto' }}
          transition={prefersReducedMotion ? { duration: 0 } : MESSAGE_TRANSITION}
        >
          {/* Hidden while empty so its top spacing collapses with it rather than standing alone */}
          <div ref={messageRef} className="pt-1 empty:hidden">
            {error ? (
              <Typography className="text-red-main" variant="caption">
                {error}
              </Typography>
            ) : warning ? (
              <Typography
                className="text-orange-text-dark"
                data-testid={htmlFor && `${htmlFor}-warning`}
                variant="caption"
              >
                {warning}
              </Typography>
            ) : (
              helperText
            )}
          </div>
        </motion.div>
      </div>
      {description && !renderDescriptionInLabelGroup && (
        <Typography className="col-span-2 text-grey60" variant={isHeaderLabel ? 'body2' : 'caption'}>
          {description}
        </Typography>
      )}
    </div>
  );
};
